<script>
	import thumb_TNTM7OuuYWw from '$lib/Components/Youtube/images/TNTM7OuuYWw.jpg?w=610';

	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import closeupCustomerInstallingIndowWindowInsert from './images/closeupCustomerInstallingIndowWindowInsert.png';
	import closeupIndowInsertCreatingCompressionSeal from './images/closeupIndowInsertCreatingCompressionSeal.jpg';
	import howMagneticStormWindowsAttachWindowFrame from './images/howMagneticStormWindowsAttachWindowFrame.jpg';
</script>

<Section>
	<p>
		What difference is a particular window attachment going to make to the
		comfort of your home? How effective is a magnetic seal or compression seal
		when used on a storm window? We’ll answer these questions in this blog and
		why Indow decided against magnetic window insulation.
	</p>
	<Youtube posterImageSrc={thumb_TNTM7OuuYWw} videoId="TNTM7OuuYWw" />
</Section>

<Section>
	<h2>Why We Said No to Magnetic Seal</h2>
	<p>
		When Indow first designed our window inserts, we considered putting magnets
		inside our compression tube, but we found that a magnetic window insulation
		created a lot of design flaws.
	</p>
	<div class="grid gap-y-2 text-lg">
		<p class="text-indow-blue! italic">
			“It’s not very attractive, it’s cumbersome to install, and that metal
			frame remains in your window frame after you remove the insert and that’s
			ugly. So there were three things we didn’t like about the magnetic
			system.”
		</p>
		<p>-Sam Pardue, Indow Founder</p>
	</div>
	<p>
		First, for a magnetic seal to form, magnets have to grab hold of something.
		That something is permanently attached to your window frame—generally a
		metal bracket fixed to the entire window frame needing many holes drilled
		into it. So it’s cumbersome to install.
	</p>
	<p>
		Second, magnetic window insulation is not attractive either with the window
		insert installed or removed. If you take the window insert off, you still
		have a big metal bracket surrounding your window.
	</p>
	<Image
		class="w-full"
		alt="diagram of a magnetic window insulation system"
		src={howMagneticStormWindowsAttachWindowFrame}
	/>
	<p>
		Third, the acrylic used in most window inserts expands and contracts with
		the temperature. While the window insert and attached magnets move, the
		metal frame does not. So, whatever magnetic seal there was is broken and the
		whole panel can pop off.
	</p>
	<p>
		Magnets attract in one direction, but compression tube creates <a
			href="https://en.wikipedia.org/wiki/Hooke%27s_law"
			target="_blank"
			rel="external">spring force</a
		> all the way around the window frame. It absorbs expansion and contraction in
		every direction and creates friction and resistance against air pressure.
	</p>
	<p>
		So, we removed the magnet and created a window insert that relies on
		compression seal alone. The compression tube can do everything we need and
		the simplest solution is often the best solution.
	</p>
	<Image
		alt="woman installing Indow insert in window to create compression seal"
		src={closeupCustomerInstallingIndowWindowInsert}
	/>
</Section>

<Section>
	<h2>Why Compression Seal is Better Than Magnetic Seal</h2>
	<p>
		The compression tube around our acrylic panels is the spring force that
		holds in insert in place. It absorbs all thermal expansion caused by
		temperature change. This ability to expand and contract creates a near
		airtight seal no matter the shape, size, or condition of your window. The
		compression tube is a seal against drafts and heat, and it keeps conditioned
		air inside.
	</p>
	<Image
		alt="close up of Indow window insert creating compression seal"
		src={closeupIndowInsertCreatingCompressionSeal}
	/>
	<p>
		A compression seal is stronger than a magnetic seal because it is better at
		dealing with out of square windows. If your home is older and has had time
		to settle, it most likely hasn’t settled perfectly straight – this causes
		out of square windows.
	</p>
	<div class="grid gap-y-2 text-lg">
		<p class="text-indow-blue! italic">
			“The problem is that magnetic systems don’t always deal really easily with
			the fact that most window frames are really out of square. So sometimes
			you get a gap up in the corner, if it’s a really out of square window.
			With our system we’re going to make the insert the exact shape of the
			window opening, you’re going to get very tight seal all the way around—a
			nice even compression.”
		</p>
		<p>– Sam Pardue, Indow Founder</p>
	</div>
	<p>
		Magnetic seals can get gaps because they were made for a perfectly square
		window frame. Compression seal adjusts for the out of square window so you
		continue to have a tight seal and even compression.
	</p>
	<p>
		Our compression seal can accommodate expansion and contraction from heat and
		window frame inconsistencies without altering the window at all. Our
		compression seal is stronger than magnetic seal, fits any shape of window,
		and requires no hardware.
	</p>
	<p>
		For more information read about our <a
			href="/resources/blog/magnetic-storm-windows-pros-and-cons"
			>compression system</a
		>.
	</p>
</Section>

<Reviews />
